/**
 * MosaicRubricTrendCard.js — v6.32.167
 *
 * Rolling MOSAIC content engine pedagogy-rubric trend across every oral capture for a
 * student. Plots 4 axes (grammar / content_coherence / register /
 * vocabulary_depth) on the 1-5 MOSAIC ladder over time, surfaces the
 * latest teacher_note, and shows the first → latest delta per axis.
 *
 * Designed to be the principal-demo artifact when a student has 2+
 * oral captures spaced weeks apart.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ArrowUp, ArrowDown, Minus, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AXES = [
  { key: 'grammar',           label: 'Grammar',     color: '#7c3aed' },  // violet
  { key: 'content_coherence', label: 'Content',     color: '#0ea5e9' },  // sky
  { key: 'register',          label: 'Register',    color: '#f59e0b' },  // amber
  { key: 'vocabulary_depth',  label: 'Vocabulary',  color: '#10b981' },  // emerald
];

const MultiLineSparkline = ({ series }) => {
  // series: { grammar: [{at, value}], ... }
  const lens = AXES.map((a) => (series[a.key] || []).length);
  const maxLen = Math.max(...lens, 0);
  if (maxLen < 2) {
    return (
      <div className="text-[11px] italic text-gray-400 px-3 py-6 text-center" data-testid="rubric-trend-empty">
        Capture at least 2 oral recordings for the same student to see the trend.
      </div>
    );
  }
  const W = 480, H = 110, PAD_X = 26, PAD_Y = 12;
  const minY = 1, maxY = 5;
  const toX = (i, n) => PAD_X + (i / Math.max(1, n - 1)) * (W - 2 * PAD_X);
  const toY = (v) => H - PAD_Y - ((v - minY) / (maxY - minY)) * (H - 2 * PAD_Y);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" data-testid="rubric-trend-sparkline">
      {/* Gridlines + Y axis labels at 1-5 */}
      {[1, 2, 3, 4, 5].map((l) => (
        <g key={l}>
          <line x1={PAD_X} x2={W - PAD_X} y1={toY(l)} y2={toY(l)} stroke="#e5e7eb" strokeWidth="0.5" />
          <text x={6} y={toY(l) + 3} fontSize="8" fill="#9ca3af">L{l}</text>
        </g>
      ))}
      {AXES.map((axis) => {
        const pts = series[axis.key] || [];
        if (pts.length === 0) return null;
        const d = pts
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i, pts.length)} ${toY(p.value)}`)
          .join(' ');
        return (
          <g key={axis.key}>
            <path d={d} fill="none" stroke={axis.color} strokeWidth="2" />
            {pts.map((p, i) => (
              <circle key={i} cx={toX(i, pts.length)} cy={toY(p.value)} r="2.5" fill={axis.color} />
            ))}
          </g>
        );
      })}
    </svg>
  );
};

const DeltaPill = ({ delta }) => {
  if (delta == null) return <span className="text-gray-300">—</span>;
  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
  const cls = delta > 0
    ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
    : delta < 0 ? 'text-rose-700 bg-rose-50 border-rose-300'
    : 'text-gray-600 bg-gray-50 border-gray-300';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 border ${cls} text-[10px] font-bold tabular-nums`}>
      <Icon className="w-3 h-3" />
      {delta > 0 ? `+${delta}` : delta}
    </span>
  );
};

const MosaicRubricTrendCard = ({ studentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true); setError(null);
    axios.get(`${API}/mosaic/rubric-trend/${studentId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load rubric trend'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return (
    <div className="text-xs text-gray-500 p-3 flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Loading rubric trend…
    </div>
  );
  if (error) return <div className="text-xs text-rose-700 p-3">{error}</div>;
  if (!data || data.n === 0) {
    return (
      <div className="border border-gray-200 p-3 text-xs text-gray-500 italic" data-testid="mosaic-rubric-trend-card">
        No MOSAIC content engine rubric data yet. Run an Oral Language Capture (≥5 words) to unlock the rubric trend.
      </div>
    );
  }

  const summary = data.summary || {};

  return (
    <div className="border-2 border-violet-300 bg-violet-50/30" data-testid="mosaic-rubric-trend-card">
      <div className="px-3 py-2 bg-violet-700 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-[11px] font-bold uppercase tracking-widest">MOSAIC content engine Rubric Trend</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-violet-200" data-testid="rubric-trend-n">
          {data.n} capture{data.n === 1 ? '' : 's'}
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Per-axis summary row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {AXES.map((a) => {
            const s = summary[a.key] || {};
            return (
              <div key={a.key} className="border border-gray-200 bg-white p-2" data-testid={`rubric-trend-axis-${a.key}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase text-gray-500 tracking-wider">
                  <span className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                  {a.label}
                </div>
                <div className="flex items-baseline gap-1.5 mt-0.5">
                  <span className="text-xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {s.latest ?? '—'}
                  </span>
                  <DeltaPill delta={s.delta} />
                </div>
                <div className="text-[10px] text-gray-500">
                  avg {s.average ?? '—'} · n={s.n ?? 0}
                </div>
              </div>
            );
          })}
        </div>

        <MultiLineSparkline series={data.series || {}} />

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-wider text-gray-600">
          {AXES.map((a) => (
            <span key={a.key} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
              {a.label}
            </span>
          ))}
        </div>

        {/* Latest teacher note */}
        {data.latest_note ? (
          <div
            className="text-sm text-gray-800 italic border-l-4 border-violet-400 pl-2 bg-white py-1"
            data-testid="rubric-trend-latest-note"
          >
            <div className="text-[10px] uppercase tracking-widest text-gray-500 not-italic mb-0.5">
              Most-recent MOSAIC content engine note
            </div>
            "{data.latest_note}"
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MosaicRubricTrendCard;
